import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { StatusBadge, UndoButton } from '@/components/common';
import { EMBRYO_POSITION } from '@/constants';
import type { ColDef } from '@/grid/types';
import { dateOnlyFormatter } from '@/grid/value-formatters/dateOnlyFormatter';
import {
  type OnFarmImplantPregCheckRow,
  PREG_CHECK_RESULT,
} from '@/services/on-farm-implant';

import { useSourceTypeLabel } from '../ImplantQueueTab/columnDefs';
import { useImplantQueueFieldOptions } from '../ImplantQueueTab/fieldOptions';
import { UndoInputCellEditor } from '../ImplantQueueTab/UndoInputCellEditor';

export const PREG_CHECK_COLUMN_NUMBERS = [1, 2, 3, 4, 5] as const;

export const pregCheckResultColors = {
  Pregnant: { bg: 'bg-green-100', text: 'text-green-700' },
  Open: { bg: 'bg-amber-100', text: 'text-amber-700' },
  Compromised: { bg: 'bg-red-100', text: 'text-red-700' },
  'Left the Herd': { bg: 'bg-gray-100', text: 'text-gray-700' },
};

export const implantStatusColors = {
  Implanted: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  Pregnant: { bg: 'bg-green-100', text: 'text-green-700' },
  Open: { bg: 'bg-amber-100', text: 'text-amber-700' },
  Calved: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  Aborted: { bg: 'bg-red-100', text: 'text-red-700' },
};

export const RESULT_OPTIONS = [
  { value: PREG_CHECK_RESULT.PREGNANT, key: 'pregnant' },
  { value: PREG_CHECK_RESULT.OPEN, key: 'open' },
  { value: PREG_CHECK_RESULT.COMPROMISED, key: 'compromised' },
  { value: PREG_CHECK_RESULT.LEFT_THE_HERD, key: 'leftTheHerd' },
] as const;

export type PregCheckEditField = 'result' | 'checkDate' | 'checkedById';

export const PREG_CHECK_EDIT_FIELDS: readonly PregCheckEditField[] = [
  'result',
  'checkDate',
  'checkedById',
];

const COL_ID_SUFFIXES: Record<PregCheckEditField, string> = {
  result: 'Result',
  checkDate: 'Date',
  checkedById: 'CheckedBy',
};

export const pregCheckColId = (n: number, field: PregCheckEditField) =>
  `pc${n}${COL_ID_SUFFIXES[field]}`;

export const parsePregCheckColId = (
  colId: string,
): { checkNumber: number; field: PregCheckEditField } | null => {
  const match = /^pc(\d)(Result|Date|CheckedBy)$/.exec(colId);
  if (!match) return null;
  const field = PREG_CHECK_EDIT_FIELDS.find(
    (f) => COL_ID_SUFFIXES[f] === match[2],
  )!;
  return { checkNumber: Number(match[1]), field };
};

export type PregCheckEditHistory = Map<string, number | string>;

export const pregCheckHistoryKey = (rowId: string, colId: string) =>
  `${rowId}:${colId}`;

export const checkFor = (
  row: OnFarmImplantPregCheckRow | undefined,
  n: number,
) => row?.checks.find((check) => check.checkNumber === n);

const hasCheck = (row: OnFarmImplantPregCheckRow | undefined, n: number) =>
  !!checkFor(row, n)?.checkDate;

const isResultEditable = (
  row: OnFarmImplantPregCheckRow | undefined,
  n: number,
) => hasCheck(row, n) && !checkFor(row, n)?.isPregnancyLoss;

export const canRecordPregnancyLoss = (row: OnFarmImplantPregCheckRow) => {
  const recorded = row.checks.filter((check) => check.checkDate);
  const latest = recorded.at(-1);
  return (
    latest?.result === PREG_CHECK_RESULT.PREGNANT &&
    recorded.length < PREG_CHECK_COLUMN_NUMBERS.length &&
    row.implantStatusName !== 'Calved' &&
    row.implantStatusName !== 'Aborted'
  );
};

type PregCheckColumnOptions = {
  history?: PregCheckEditHistory;
  onRevert?: (
    row: OnFarmImplantPregCheckRow,
    checkNumber: number,
    field: PregCheckEditField,
    previous: number | string,
  ) => void;
};

const createCallbackStore = () => {
  let current: PregCheckColumnOptions = {};
  return {
    get: () => current,
    set: (next: PregCheckColumnOptions) => {
      current = next;
    },
  };
};

export const usePregCheckColumnDefs = ({
  history,
  onRevert,
}: PregCheckColumnOptions = {}): ColDef<OnFarmImplantPregCheckRow>[] => {
  const { t } = useTranslation();
  const sourceTypeLabel = useSourceTypeLabel();
  const { techOptions } = useImplantQueueFieldOptions();
  const [callbacks] = useState(createCallbackStore);

  useEffect(() => {
    callbacks.set({ history, onRevert });
  }, [callbacks, history, onRevert]);

  return useMemo(() => {
    const clSideLabel = (value: number | null | undefined) => {
      if (value === EMBRYO_POSITION.LEFT)
        return t('onFarmImplant.implantQueue.clSides.left');
      if (value === EMBRYO_POSITION.RIGHT)
        return t('onFarmImplant.implantQueue.clSides.right');
      return '';
    };

    const resultLabel = (value: unknown) => {
      const option = RESULT_OPTIONS.find((o) => o.value === value);
      return option ? t(`onFarmImplant.pregCheck.results.${option.key}`) : '';
    };

    const techNames = new Map(techOptions.map((o) => [o.value, o.label]));
    const techLabel = (value: unknown, row?: OnFarmImplantPregCheckRow) => {
      if (typeof value !== 'string' || !value) return '';
      const fromRow = row?.checks.find(
        (check) => check.checkedById === value,
      )?.checkedByName;
      return fromRow ?? techNames.get(value) ?? '';
    };

    const undoButton = (
      row: OnFarmImplantPregCheckRow,
      n: number,
      field: PregCheckEditField,
      beforeRevert?: () => void,
    ) => {
      const key = pregCheckHistoryKey(
        row.sessionQueueId,
        pregCheckColId(n, field),
      );
      const rowHistory = callbacks.get().history;
      if (!rowHistory?.has(key)) return null;
      const previous = rowHistory.get(key)!;
      const label =
        field === 'result'
          ? resultLabel(previous)
          : field === 'checkDate'
            ? dateOnlyFormatter({ value: previous as string })
            : techLabel(previous);
      const title = t('onFarmImplant.implantQueue.revertTitle', {
        value: label,
      });
      return (
        <UndoButton
          title={title}
          onUndo={() => {
            beforeRevert?.();
            callbacks.get().onRevert?.(row, n, field, previous);
          }}
        />
      );
    };

    const editableCell = (n: number) => ({
      editable: (params: { data?: OnFarmImplantPregCheckRow }) =>
        hasCheck(params.data, n),
      cellClassRules: {
        'editable-cell': (params: { data?: OnFarmImplantPregCheckRow }) =>
          hasCheck(params.data, n),
      },
    });

    const checkColumns = PREG_CHECK_COLUMN_NUMBERS.flatMap(
      (n): ColDef<OnFarmImplantPregCheckRow>[] => [
        {
          colId: pregCheckColId(n, 'result'),
          headerName: t('onFarmImplant.pregCheck.checkResult', { number: n }),
          flex: 0.9,
          minWidth: 130,
          hide: n > 1,
          editable: (params) => isResultEditable(params.data, n),
          cellClassRules: {
            'editable-cell': (params) => isResultEditable(params.data, n),
          },
          valueGetter: (params) => checkFor(params.data, n)?.result ?? null,
          valueFormatter: (params) => resultLabel(params.value),
          filter: 'agSetColumnFilter',
          filterValueGetter: (params) =>
            checkFor(params.data, n)?.resultName ?? '',
          getQuickFilterText: (params) =>
            checkFor(params.data, n)?.resultName ?? '',
          cellRenderer: (params: {
            data?: OnFarmImplantPregCheckRow;
            value?: number | null;
          }) => {
            const check = checkFor(params.data, n);
            const name = check?.resultName;
            return (
              <span className="flex w-full items-center justify-between gap-1">
                <span className="flex items-center gap-1">
                  {name && (
                    <StatusBadge
                      label={name}
                      colorMap={pregCheckResultColors}
                    />
                  )}
                  {check?.isPregnancyLoss && (
                    <span
                      className="text-[10px] font-medium text-red-600"
                      title={t('onFarmImplant.pregCheck.loss.marker')}
                    >
                      {t('onFarmImplant.pregCheck.loss.markerShort')}
                    </span>
                  )}
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  {params.data && undoButton(params.data, n, 'result')}
                  {isResultEditable(params.data, n) && (
                    <i className="pi pi-chevron-down shrink-0 text-[10px] text-slate-400" />
                  )}
                </span>
              </span>
            );
          },
          cellEditor: 'agRichSelectCellEditor',
          cellEditorParams: {
            values: RESULT_OPTIONS.map((option) => option.value),
            formatValue: resultLabel,
          },
        },
        {
          colId: pregCheckColId(n, 'checkDate'),
          headerName: t('onFarmImplant.pregCheck.checkDate', { number: n }),
          flex: 0.8,
          minWidth: 130,
          hide: n > 1,
          filter: false,
          ...editableCell(n),
          valueGetter: (params) =>
            checkFor(params.data, n)?.checkDate?.slice(0, 10) ?? null,
          valueFormatter: dateOnlyFormatter,
          cellRenderer: (params: {
            data?: OnFarmImplantPregCheckRow;
            valueFormatted?: string | null;
          }) => (
            <span className="flex w-full items-center justify-between gap-1">
              <span className="truncate">{params.valueFormatted ?? ''}</span>
              {params.data && undoButton(params.data, n, 'checkDate')}
            </span>
          ),
          cellEditor: UndoInputCellEditor,
          cellEditorParams: {
            inputType: 'date',
            renderUndo: (
              row: OnFarmImplantPregCheckRow,
              beforeRevert: () => void,
            ) => undoButton(row, n, 'checkDate', beforeRevert),
          },
        },
        {
          colId: pregCheckColId(n, 'checkedById'),
          headerName:
            n === 1
              ? t('onFarmImplant.pregCheck.checkedBy')
              : t('onFarmImplant.pregCheck.checkCheckedBy', { number: n }),
          flex: 1,
          minWidth: 140,
          hide: n > 1,
          ...editableCell(n),
          valueGetter: (params) =>
            checkFor(params.data, n)?.checkedById ?? null,
          valueFormatter: (params) => techLabel(params.value, params.data),
          filter: 'agSetColumnFilter',
          filterValueGetter: (params) =>
            checkFor(params.data, n)?.checkedByName ?? '',
          getQuickFilterText: (params) =>
            checkFor(params.data, n)?.checkedByName ?? '',
          cellRenderer: (params: {
            data?: OnFarmImplantPregCheckRow;
            valueFormatted?: string | null;
          }) => (
            <span className="flex w-full items-center justify-between gap-1">
              <span className="truncate">{params.valueFormatted ?? ''}</span>
              <span className="flex shrink-0 items-center gap-1">
                {params.data && undoButton(params.data, n, 'checkedById')}
                {hasCheck(params.data, n) && (
                  <i className="pi pi-chevron-down shrink-0 text-[10px] text-slate-400" />
                )}
              </span>
            </span>
          ),
          cellEditor: 'agRichSelectCellEditor',
          cellEditorParams: {
            values: techOptions.map((option) => option.value),
            formatValue: (value: unknown) =>
              techNames.get(value as string) ?? '',
            allowTyping: true,
            filterList: true,
            searchType: 'matchAny',
            highlightMatch: true,
          },
        },
      ],
    );

    return [
      {
        colId: 'select',
        headerName: '',
        width: 44,
        maxWidth: 44,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        suppressMovable: true,
        lockPosition: true,
        filter: false,
        sortable: false,
        resizable: false,
        suppressColumnsToolPanel: true,
      },
      {
        field: 'recipientTag',
        headerName: t('onFarmImplant.pregCheck.recipientTag'),
        flex: 1,
        minWidth: 130,
        filter: 'agTextColumnFilter',
        valueFormatter: (params) => params.value ?? '—',
      },
      {
        field: 'embryoName',
        headerName: t('onFarmImplant.pregCheck.embryo'),
        flex: 1.1,
        minWidth: 140,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'implantDate',
        headerName: t('onFarmImplant.pregCheck.implantDate'),
        flex: 0.8,
        minWidth: 120,
        filter: false,
        valueFormatter: dateOnlyFormatter,
      },
      {
        field: 'sourceType',
        headerName: t('onFarmImplant.pregCheck.source'),
        flex: 0.8,
        minWidth: 110,
        filter: 'agSetColumnFilter',
        valueGetter: (params) =>
          params.data ? sourceTypeLabel(params.data.sourceType) : '',
      },
      {
        field: 'matingName',
        headerName: t('onFarmImplant.pregCheck.mating'),
        flex: 1.1,
        minWidth: 140,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'donorName',
        headerName: t('onFarmImplant.pregCheck.donor'),
        flex: 1,
        minWidth: 120,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'sireName',
        headerName: t('onFarmImplant.pregCheck.sire'),
        flex: 1,
        minWidth: 120,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'accountName',
        headerName: t('onFarmImplant.pregCheck.account'),
        flex: 1.1,
        minWidth: 140,
        filter: 'agSetColumnFilter',
      },
      {
        field: 'technicianName',
        headerName: t('onFarmImplant.pregCheck.implantTech'),
        flex: 1,
        minWidth: 120,
        filter: 'agSetColumnFilter',
      },
      {
        field: 'clSide',
        headerName: t('onFarmImplant.pregCheck.clSide'),
        flex: 0.5,
        minWidth: 80,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => clSideLabel(params.data?.clSide),
      },
      ...checkColumns,
      {
        field: 'implantStatusName',
        headerName: t('onFarmImplant.pregCheck.implantStatus'),
        flex: 0.9,
        minWidth: 130,
        filter: 'agSetColumnFilter',
        cellRenderer: (params: { value?: string | null }) =>
          params.value ? (
            <StatusBadge label={params.value} colorMap={implantStatusColors} />
          ) : null,
      },
    ];
  }, [t, sourceTypeLabel, techOptions, callbacks]);
};
